<template>
    <div class="label-print">
        <div class="print-toolbar">
            <el-button type="primary" size="small" icon="el-icon-printer" @click="doPrint">打印</el-button>
            <el-radio-group v-model="paperMode" size="small" @change="handleLayoutChange">
                <el-radio-button label="a4">A4 纸</el-radio-button>
                <el-radio-button label="label">标签纸 100×80</el-radio-button>
            </el-radio-group>
            <div class="toolbar-item" v-if="paperMode === 'a4'">
                <span class="toolbar-label">每页张数</span>
                <el-select v-model="perPage" size="small" style="width: 90px" @change="handleLayoutChange">
                    <el-option v-for="n in perPageOptions" :key="n" :label="n + ' 张'" :value="n"></el-option>
                </el-select>
            </div>
            <span class="toolbar-tip" v-if="cards.length">
                共 {{ cards.length }} 张标识卡 / {{ pages.length }} 页（{{ layoutText }}）{{ generating ? '（正在生成二维码…）' : '' }}
            </span>
        </div>
        <div class="sheets">
            <div class="sheet" v-for="(page, pi) in pages" :key="pi" :class="{ 'sheet-label': paperMode === 'label' }">
                <div class="card-grid" :style="gridStyle">
                    <div class="label-cell" v-for="(cell, ci) in page" :key="ci">
                        <div class="card-scale" v-if="cell" :style="cardScaleStyle">
                            <div
                                class="label-card"
                                :class="{ 'label-card-compact': paperMode === 'label', 'label-card-large': paperMode !== 'label' && perPage > 6 }"
                                :style="cardStyle"
                            >
                                <!-- 顶部：公司图片（左） + 零件号二维码（右），不框线 -->
                                <div class="card-head">
                                    <img class="card-logo" src="@/assets/proc_label_logo.png" alt="" />
                                    <img class="card-qr" v-if="cell.qr" :src="cell.qr" alt="" />
                                </div>
                                <!-- 公司图片下方：标识卡内容，单独方框，逐行横线分隔 -->
                                <table class="card-content">
                                    <tbody>
                                        <tr class="card-title-row">
                                            <td>外发加工送货标识卡</td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <div class="card-field">
                                                    <span class="card-label">供应商名称：</span><span class="card-value">{{ cell.supplierName }}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <div class="card-field">
                                                    <span class="card-label">零件号：</span><span class="card-value">{{ cell.materialCode }}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <div class="card-field">
                                                    <span class="card-label">零件名称：</span><span class="card-value">{{ cell.materialName }}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <div class="card-field">
                                                    <span class="card-label">回货数量：</span><span class="card-value">{{ cell.qty }}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr class="card-row-project">
                                            <td>
                                                <div class="card-field card-field-nowrap">
                                                    <span class="card-label">项目编号、图号：</span><span class="card-value">{{ projectText(cell) }}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <div class="card-field">
                                                    <span class="card-label">备注：</span>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import QRCode from "qrcode";
import { getOutSourceSendApi, getOutSourceSendItemApi } from "@/Api/procApi";

//标识卡设计尺寸（mm），实际打印时按格子大小等比缩放
const CARD_WIDTH = 100.1;
//与 A4 六张版式的格子比例一致；高度含底部 2mm 空白，缩放后内容正好铺满并留出底部间距
const CARD_HEIGHT = 95.6;
//A4 可打印区域：210 x 296.5，四周边距各 5mm
const A4_WIDTH = 200;
const A4_HEIGHT = 286.5;
//标签纸尺寸（四周留 5mm 打印边距）
const LABEL_WIDTH = 100;
const LABEL_HEIGHT = 80;
const LABEL_PADDING = 5;
//卡片底部留白（mm）
const CARD_BOTTOM_GAP = 2;
//标签纸可用区域与卡片设计尺寸：卡片等比放大 2mm，缩放后铺满可用区域并留出底部间距
const LABEL_SLOT_WIDTH = LABEL_WIDTH - LABEL_PADDING * 2;
const LABEL_SLOT_HEIGHT = LABEL_HEIGHT - LABEL_PADDING * 2;
const LABEL_CARD_HEIGHT = LABEL_SLOT_HEIGHT + CARD_BOTTOM_GAP;
const LABEL_CARD_WIDTH = (LABEL_CARD_HEIGHT * LABEL_SLOT_WIDTH) / LABEL_SLOT_HEIGHT;
//A4 每页张数可选值与对应的行列布局（行 x 列）
const A4_GRID = {
    6: [3, 2],
    8: [4, 2],
    10: [5, 2],
};
const PER_PAGE_OPTIONS = [6, 8, 10];

export default {
    name: "procSendLabel",
    data() {
        return {
            cards: [],
            generating: false,
            //纸张类型：a4 普通 A4 纸；label 标签纸 100×80（一页一张）
            paperMode: "a4",
            //A4 每页打印张数：仅支持 6、8、10 张
            perPage: 6,
            perPageOptions: PER_PAGE_OPTIONS,
        };
    },
    computed: {
        grid() {
            return this.paperMode === "label" ? [1, 1] : A4_GRID[this.perPage] || A4_GRID[6];
        },
        cols() {
            return this.grid[1];
        },
        rows() {
            return this.grid[0];
        },
        //每张标识卡的设计尺寸（mm）
        cardSize() {
            if (this.paperMode === "label") {
                return { width: LABEL_CARD_WIDTH, height: LABEL_CARD_HEIGHT };
            }
            //7 张以上每页的格子偏扁，卡片按格子宽度反推设计宽度，打印后横向铺满格子
            if (this.perPage > 6) {
                const slot = this.slotSize;
                return { width: (slot.width * CARD_HEIGHT) / slot.height, height: CARD_HEIGHT };
            }
            return { width: CARD_WIDTH, height: CARD_HEIGHT };
        },
        //每张标识卡可用格子尺寸（mm）
        slotSize() {
            if (this.paperMode === "label") {
                return { width: LABEL_WIDTH - LABEL_PADDING * 2, height: LABEL_HEIGHT - LABEL_PADDING * 2 };
            }
            return { width: A4_WIDTH / this.cols, height: A4_HEIGHT / this.rows };
        },
        //等比缩放比例，保证整张标识卡完整放入格子
        cardScale() {
            return Math.min(this.slotSize.width / this.cardSize.width, this.slotSize.height / this.cardSize.height);
        },
        cardStyle() {
            return {
                width: this.cardSize.width + "mm",
                height: this.cardSize.height + "mm",
                transform: `scale(${this.cardScale})`,
            };
        },
        //缩放后的占位尺寸，用于在格子内居中
        cardScaleStyle() {
            return {
                width: this.cardSize.width * this.cardScale + "mm",
                height: this.cardSize.height * this.cardScale + "mm",
            };
        },
        gridStyle() {
            return {
                gridTemplateColumns: `repeat(${this.cols}, 1fr)`,
                gridTemplateRows: `repeat(${this.rows}, 1fr)`,
            };
        },
        layoutText() {
            return this.paperMode === "label" ? "标签纸 100×80，每页 1 张" : `A4，每页 ${this.perPage} 张`;
        },
        pages() {
            const pages = [];
            //按用户选择的张数分页；格子容量大于张数时补空格子
            const perPage = this.paperMode === "label" ? 1 : this.perPage;
            const capacity = this.cols * this.rows;
            for (let i = 0; i < this.cards.length; i += perPage) {
                const slice = this.cards.slice(i, i + perPage);
                while (slice.length < capacity) {
                    slice.push(null);
                }
                pages.push(slice);
            }
            return pages;
        },
    },
    methods: {
        //项目编号、图号：合同号 + 「、」+ 图号
        projectText(cell) {
            return [cell.trackNumber, cell.mapCode].filter((v) => v !== undefined && v !== null && v !== "").join("、");
        },
        //生成二维码（内容为“项目编号、零件号”）
        buildQr(text) {
            return QRCode.toDataURL(text || "", {
                width: 150,
                margin: 1,
                errorCorrectionLevel: "M",
                color: { dark: "#000000", light: "#FFFFFF" },
            }).catch(() => "");
        },
        //纸张切换或张数变化时刷新打印页面尺寸，并记住该用户的选择
        handleLayoutChange() {
            this.applyPageSize();
            localStorage.setItem("procLabelLayout", JSON.stringify({ paperMode: this.paperMode, perPage: this.perPage }));
        },
        //@page 无法写在组件样式里，按所选纸张动态注入
        applyPageSize() {
            let style = document.getElementById("proc-label-page-size");
            if (!style) {
                style = document.createElement("style");
                style.id = "proc-label-page-size";
                document.head.appendChild(style);
            }
            style.textContent =
                this.paperMode === "label"
                    ? `@page { size: ${LABEL_WIDTH}mm ${LABEL_HEIGHT}mm; margin: 0; }`
                    : "@page { size: A4 portrait; margin: 0; }";
        },
        doPrint() {
            this.applyPageSize();
            this.printWhenReady();
        },
        //等待卡面图片（公司 logo / 二维码）解码后再打印，避免出现空白页
        printWhenReady() {
            const imgs = Array.from(this.$el.querySelectorAll("img"));
            const pending = imgs
                .map((img) => {
                    if (img.complete && img.naturalWidth > 0) return null;
                    if (img.decode) return img.decode().catch(() => {});
                    return new Promise((resolve) => {
                        img.addEventListener("load", resolve, { once: true });
                        img.addEventListener("error", resolve, { once: true });
                    });
                })
                .filter(Boolean);
            Promise.all(pending).then(() => {
                setTimeout(() => window.print(), 150);
            });
        },
        //加载单张送货单的标识卡数据（供应商名称、合同号按单据取，随卡片一起保存）
        async loadOne(formCode) {
            const headRes = await getOutSourceSendApi({ formCode, pageSize: 1, currentPage: 1 }, this);
            const head = (headRes.records && headRes.records[0]) || {};
            const supplierName = head.procSupplier ? head.procSupplier.supplierName : "";
            const trackNumber = head.project ? head.project.trackNumber : "";
            const itemRes = await getOutSourceSendItemApi({ formCode, pageSize: 999, currentPage: 1 }, this);
            return ((itemRes && itemRes.records) || []).map((item) => {
                const materialCode = (item.material && item.material.materialCode) || "";
                return {
                    supplierName,
                    trackNumber,
                    materialCode,
                    materialName: (item.material && item.material.materialName) || "",
                    mapCode: item.mapCode || "",
                    qty: item.qty,
                    qr: "",
                    qrText: [trackNumber, materialCode].filter((v) => v !== undefined && v !== null && v !== "").join("、"),
                };
            });
        },
        async loadData() {
            const raw = this.$route.query.formCode;
            const formCodes = (Array.isArray(raw) ? raw : raw ? [raw] : []).filter(Boolean);
            if (!formCodes.length) return;
            this.generating = true;
            //逐张单据加载，先渲染文字内容，边加载边可见
            for (const formCode of formCodes) {
                try {
                    this.cards = this.cards.concat(await this.loadOne(formCode));
                    await this.$nextTick();
                } catch (e) {
                    //单张单据失败不影响其它单据
                }
            }
            //再并行生成二维码，逐张回填，避免长时间空白等待
            await Promise.all(this.cards.map((card) => this.buildQr(card.qrText).then((qr) => this.$set(card, "qr", qr))));
            //总张数不超过 6 张时，默认按每页 6 张排版
            if (this.cards.length <= 6) this.perPage = 6;
            this.generating = false;
        },
    },
    mounted() {
        //恢复该用户上次选择的纸张与每页张数
        try {
            const saved = JSON.parse(localStorage.getItem("procLabelLayout") || "{}");
            if (saved.paperMode === "a4" || saved.paperMode === "label") this.paperMode = saved.paperMode;
            if (PER_PAGE_OPTIONS.includes(Number(saved.perPage))) this.perPage = Number(saved.perPage);
        } catch (e) {
            //忽略历史数据解析异常，使用默认值
        }
        this.applyPageSize();
        this.loadData();
    },
    beforeDestroy() {
        const style = document.getElementById("proc-label-page-size");
        if (style) style.remove();
    },
};
</script>

<style scoped>
.label-print {
    background: #525659;
    min-height: 100vh;
    padding: 12px 0 30px;
}

.print-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 0 0 12px 40px;
}

.toolbar-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

.toolbar-label {
    color: #fff;
    font-size: 13px;
}

.toolbar-tip {
    color: #fff;
    font-size: 13px;
}

.sheets {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

/* A4 纸张，四周边距各 5mm */
.sheet {
    width: 210mm;
    height: 296.5mm;
    padding: 5mm;
    box-sizing: border-box;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

/* 标签纸 100 x 80，一页一张，四周留 5mm 打印边距 */
.sheet-label {
    width: 100mm;
    height: 80mm;
    padding: 5mm;
}

/* 按行列数铺满整页，卡片在各自格子内等比缩放并居中 */
.card-grid {
    display: grid;
    width: 100%;
    height: 100%;
}

.label-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.card-scale {
    position: relative;
}

/* 标识卡设计尺寸固定，打印时整体等比缩放，字号与行高同步变化 */
.label-card {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 1.2mm 2mm 2mm;
    border: 1px solid #000;
    overflow: hidden;
    color: #000;
    font-family: "宋体", SimSun, serif;
    font-size: 11pt;
    line-height: 12pt;
    transform-origin: top left;
}

/* 顶部：公司图片（左） + 二维码（右） */
.card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 0 0 14mm;
    height: 14mm;
}

/* 按模版中公司名称文字比例显示，避免压扁 */
.card-logo {
    height: 13mm;
    max-width: 73mm;
    object-fit: contain;
    object-position: left center;
}

.card-qr {
    flex: 0 0 13mm;
    width: 13mm;
    height: 13mm;
}

/* 公司图片下方的内容区：单独方框，逐行横线分隔，自动铺满卡片剩余高度 */
.card-content {
    width: 100%;
    flex: 1 1 auto;
    border-collapse: collapse;
    table-layout: fixed;
    margin-top: 0.5mm;
}

.card-content td {
    height: 8.4mm;
    padding: 0.5mm 1.5mm;
    border: 1px solid #000;
    vertical-align: middle;
    font-family: "宋体", SimSun, serif;
    font-weight: 400;
}

/* 标题行：内容开头一行“外发加工送货标识卡”，行高略大 */
.card-title-row td {
    height: 10.5mm;
    text-align: center;
    font-weight: 900;
    font-size: 12pt;
    font-family: "宋体", SimSun, serif;
}

/* 备注行：留两行的书写空间 */
.card-content tr:last-child td {
    height: 9.5mm;
    vertical-align: top;
}

/* 标签纸 90x70 版式：压缩行高与头部，内容铺满整张标签 */
.label-card-compact {
    padding: 1mm 1.5mm 2mm;
}

/* A4 每页 7 张以上时格子偏矮，整体缩放后字号过小，这里按设计尺寸放大字号 */
.label-card-large {
    font-size: 14pt;
    line-height: 15pt;
}

.label-card-large .card-title-row td {
    font-size: 15pt;
    line-height: 15pt;
}

.label-card-compact .card-head {
    flex: 0 0 10mm;
    height: 10mm;
}

.label-card-compact .card-logo {
    height: 9mm;
    max-width: 70mm;
}

.label-card-compact .card-qr {
    flex: 0 0 9mm;
    width: 9mm;
    height: 9mm;
}

.label-card-compact .card-content td {
    height: 6mm;
}

.label-card-compact .card-title-row td {
    height: 7.2mm;
}

.label-card-compact .card-content tr:last-child td {
    height: 8.5mm;
}

/* 标签纸卡片较窄，「项目编号、图号」整行仍要求同一行显示，字号略降以完整容纳 */
.label-card-compact .card-row-project td {
    font-size: 10pt;
}

.card-field {
    display: flex;
    flex-wrap: wrap;
    column-gap: 0;
    align-items: baseline;
    width: 100%;
}

.card-field > .card-label {
    white-space: nowrap;
}

.card-field > .card-value {
    min-width: 0;
    word-break: break-all;
}

/* 项目编号、图号：强制同一行显示 */
.card-row-project td {
    white-space: nowrap;
}

.card-field-nowrap {
    flex-wrap: nowrap;
}

.card-field-nowrap > .card-value {
    word-break: normal;
}

.card-label {
    font-weight: 900;
}

.card-value {
    font-weight: 400;
}

@media print {
    .label-print {
        background: #fff;
        padding: 0;
        min-height: auto;
    }

    .print-toolbar {
        display: none;
    }

    /* 宋体无原生粗体，打印时用描边加固，保证标签名与标题在纸面同样加粗 */
    .card-label,
    .card-title-row td {
        -webkit-text-stroke: 0.3px #000;
        text-stroke: 0.3px #000;
    }

    .sheets {
        display: block;
        gap: 0;
    }

    .sheet {
        box-shadow: none;
        page-break-after: always;
        break-after: page;
    }

    .sheet:last-child {
        page-break-after: auto;
        break-after: auto;
    }
}
</style>
